// @flow
import {
  useState,
  useEffect,
  useContext,
  createContext,
  useRef,
  type Node
} from 'react';

type GeoLocationType = {
  lat: number,
  lon: number
};

type ContextStateType = {
  currentLocation: void | GeoLocationType,
  isFindingCurrentLocation: boolean
};

const Context = createContext<ContextStateType>({
  isFindingCurrentLocation: true,
  currentLocation: undefined
});

// the logic for this variable is because of a difficult race condition between
// useEffect and render
let callOnLoadLocationSet = false;
const defaultLoadLocation = () => {
  callOnLoadLocationSet = true;
};
let loadLocation = defaultLoadLocation;

export const GeoLocationProvider = ({
  currentLocationRef,
  children
}: {
  children: Node,
  currentLocationRef: { current?: GeoLocationType }
}) => {
  const hasRequestedLocation = useRef(false);
  const [contextState, setContextState] = useState<ContextStateType>({
    currentLocation: undefined,
    isFindingCurrentLocation: true
  });
  const setContextStateRef = useRef(setContextState);

  useEffect(() => {
    loadLocation = () => {
      if (
        contextState.currentLocation ||
        hasRequestedLocation.current === true
      ) {
        return;
      }
      if (typeof window.navigator !== 'undefined') {
        if (!navigator.geolocation) {
          if (setContextStateRef.current) {
            setContextStateRef.current({
              currentLocation: undefined,
              isFindingCurrentLocation: false
            });
          }
        } else {
          hasRequestedLocation.current = true;
          navigator.geolocation.getCurrentPosition(
            ({ coords: { latitude, longitude } }) => {
              const foundLocation = { lat: latitude, lon: longitude };
              currentLocationRef.current = foundLocation;
              if (setContextStateRef.current) {
                setContextStateRef.current({
                  currentLocation: foundLocation,
                  isFindingCurrentLocation: false
                });
              }
            },
            (error) => {
              console.error('find geo error', error);
              if (setContextStateRef.current) {
                setContextStateRef.current({
                  currentLocation: undefined,
                  isFindingCurrentLocation: false
                });
              }
            }
          );
        }
      }
    };
    if (callOnLoadLocationSet) {
      loadLocation();
    }
    callOnLoadLocationSet = false;
    return () => {
      setContextStateRef.current = null;
      loadLocation = defaultLoadLocation;
    };
  }, []);

  return <Context.Provider value={contextState}>{children}</Context.Provider>;
};

export function useGeoLocation(): {
  currentLocation: void | GeoLocationType,
  isFindingCurrentLocation: boolean
} {
  useEffect(loadLocation, []);
  return useContext(Context);
}
